import { Checkbox } from 'components/widgets';
import LabeledCheckboxList from 'components/widgets/LabeledCheckboxList';
import PropTypes from 'prop-types';
import React, { useState } from 'react';
import Highlighter from 'react-highlight-words';
import { withLocalize } from 'react-localize-redux';
import AutoComplete from '../../components/molecules/AutoComplete';
import { MetricFieldSchema, MetricValueSchema } from '../types';
import { filterFieldsBySearch, translationPrefix } from '../utils';
import ExpandableGroup from './ExpandableGroup';
import css from './FieldGroups.css';
import SelectAll from './SelectAll';

const FieldGroups = ({ fields, allValues, onChange, enableAddFuture, translate }) => {
    const [fieldFilter, setFieldFilter] = useState('');

    const onGroupChange = (field, newState) => {
        // Find the original field to get all values, not just filtered ones
        const originalField = fields.find(f => f.label === field.label);
        onChange(
            originalField.values.map(item => item.name),
            Boolean(newState)
        );
    };

    const onMetricSelected = (id, newState) => onChange([id], Boolean(newState));

    const selectAll = newState => {
        onChange(
            allValues.map(value => value.name),
            Boolean(newState)
        );
        if (newState) {
            enableAddFuture();
        }
    };

    const filteredFields = filterFieldsBySearch(fields, fieldFilter, translate);

    return (
        <div className={css.fieldGroupsContainer}>
            <AutoComplete
                placeholder={translate(`${translationPrefix}.PLACEHOLDER.METRIC_SEARCH`)}
                selectOptions={{
                    options: [],
                    noOptionsMessage: () => null,
                    isSearchable: true,
                    inputValue: fieldFilter,
                }}
                onInputChange={setFieldFilter}
                controlledValue={fieldFilter}
            />
            <SelectAll allItems={allValues} onChange={selectAll} />
            <div className={css.scrollableContent}>
                {filteredFields.map(field => {
                    // Calculate selected values from the original field, not the filtered one
                    const originalField = fields.find(f => f.label === field.label);
                    const selectedValues = originalField.values.filter(value => value.selected);
                    const fieldChecked = selectedValues.length === originalField.values.length;
                    const fieldIndeterminate =
                        selectedValues.length > 0 && selectedValues.length < originalField.values.length;

                    // Generate a unique key based on field label and filtered values count
                    // This forces ExpandableGroup to re-render and recalculate height when content changes
                    const expandableKey = `${field.label}-${field.values.length}`;

                    return (
                        <ExpandableGroup
                            key={expandableKey}
                            title={
                                <Highlighter
                                    searchWords={[fieldFilter]}
                                    textToHighlight={translate(field.label)}
                                    autoEscape
                                    className={css.fieldTitle}
                                />
                            }
                            rightComponents={
                                <Checkbox
                                    label={translate(`${translationPrefix}.SELECTED_COUNT`, {
                                        selectedCount: selectedValues.length,
                                        totalCount: originalField.values.length,
                                    })}
                                    className={css.sectionCheckbox}
                                    labelPosition="left"
                                    checked={fieldChecked}
                                    threeStatesEnabled
                                    indeterminate={fieldIndeterminate}
                                    onChange={newState => onGroupChange(field, newState)}
                                />
                            }
                        >
                            <div>
                                <LabeledCheckboxList
                                    fields={field.values}
                                    columnsNumber={field.type === 'customEvents' ? 1 : 4}
                                    onChange={onMetricSelected}
                                    search={fieldFilter}
                                />
                            </div>
                        </ExpandableGroup>
                    );
                })}
            </div>
        </div>
    );
};

FieldGroups.propTypes = {
    fields: PropTypes.arrayOf(MetricFieldSchema.isRequired).isRequired,
    allValues: PropTypes.arrayOf(MetricValueSchema.isRequired).isRequired,
    onChange: PropTypes.func.isRequired,
    enableAddFuture: PropTypes.func.isRequired,
    translate: PropTypes.func.isRequired,
};

export default withLocalize(FieldGroups);
